import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import {
  Sparkles,
  Heart,
  Coffee,
  CheckCircle,
  Plus,
  Wrench,
  Bug,
  Zap,
  Clock,
  Star,
  Menu,
  X,
  Puzzle
} from 'lucide-react';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import SEO from './SEO';

const Changelog = () => {
  const { isDark, setIsDark } = useTheme();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const lenisRef = useRef(null);

  // Safari detection for optimized transitions
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Initialize Lenis smooth scroll
  useEffect(() => {
    const lenis = new Lenis({
      duration: isSafari ? 0.8 : 1.0,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: !isSafari, // Disable smooth scroll on Safari for better performance
      mouseMultiplier: isSafari ? 0.5 : 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
      lerp: isSafari ? 0.15 : 0.1,
    });

    lenisRef.current = lenis;

    // Reset scroll position immediately when component mounts
    lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [isSafari]);

  useEffect(() => {
    let rafId = null;
    let mouseX = 0;
    let mouseY = 0;

    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;

      // Throttle updates using requestAnimationFrame
      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          setMousePosition({ x: mouseX, y: mouseY });
          rafId = null;
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getIconForType = (type) => {
    switch (type) {
      case 'feature':
        return <Plus className="w-4 h-4" />;
      case 'improvement':
        return <Wrench className="w-4 h-4" />;
      case 'fix':
        return <Bug className="w-4 h-4" />;
      case 'performance':
        return <Zap className="w-4 h-4" />;
      default:
        return <CheckCircle className="w-4 h-4" />;
    }
  };

  const getColorForType = (type) => {
    switch (type) {
      case 'feature':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
      case 'improvement':
        return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30';
      case 'fix':
        return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
      case 'performance':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/30';
    }
  };

  const changelog = [
    {
      version: '1.7.9',
      date: 'October 22, 2025',
      title: 'Advanced Export System & Mobile Optimizations',
      changes: [
        { type: 'feature', text: 'Added comprehensive "Export All" dropdown with Backup (JSON), PDF (multi-page), and SVG (combined) options for exporting all saved palettes at once' },
        { type: 'feature', text: 'PDF export functionality for individual palettes with professional A4 landscape layout' },
        { type: 'feature', text: 'Enhanced SVG exports optimized for design software (Figma, Sketch, Illustrator, Affinity Designer) with proper structure and IDs' },
        { type: 'feature', text: 'Expanded export dropdown for individual palettes with organized sections: Design Software (SVG, PDF, Sketch, ASE) and Share & Export (Social Media)' },
        { type: 'feature', text: 'Community engagement: Added #komorebicolorplay hashtag to CTA sections across all website pages' },
        { type: 'improvement', text: 'Mobile optimization: Hidden decorative images on Story page for better mobile readability and performance' },
        { type: 'improvement', text: 'Improved export dropdown UI with helpful descriptions for each format' },
        { type: 'improvement', text: 'All export functions now show success toasts with palette counts' },
      ]
    },
    {
      version: '1.7.8',
      date: 'October 20, 2025',
      title: 'Visualizer Expansion & Color History Management',
      changes: [
        { type: 'feature', text: 'Added 6 new professional templates: E-commerce product pages, pricing tables, blog layouts, portfolio grids, email templates, and login pages' },
        { type: 'feature', text: 'New "Clear All" button in color history panel with success toast notification' },
        { type: 'feature', text: '25+ new pattern designs: Grid & line patterns, modern wave patterns, geometric shapes' },
        { type: 'feature', text: 'Renamed "Illustration" tab to "Graphical Elements" with expanded content library' },
        { type: 'improvement', text: 'Fixed aspect ratio issues in pattern displays for proportional rendering' },
        { type: 'improvement', text: 'Enhanced image effects: glassmorphism, split screens, gradient masks, decorative frames, collage layouts, tinted monochrome' },
        { type: 'improvement', text: 'Fixed card component button alignment in UI tab with flexbox layout' },
        { type: 'improvement', text: 'Added abstract blob shapes, badges & stickers, avatar elements, 3D-style illustrations, and logo concepts to graphical elements' },
      ]
    },
    {
      version: '1.7.7',
      date: 'October 18, 2025',
      title: 'Mood Builder - Brand Personality Palettes',
      changes: [
        { type: 'feature', text: 'New Mood Builder for generating palettes based on brand personality and emotional qualities' },
        { type: 'feature', text: '18 scientifically-curated mood presets: Energetic, Calm, Professional, Luxurious, Natural, Creative, Trustworthy, Modern, Friendly, Elegant, Spiritual, Bold, Playful, Minimalist, Vintage, Warm, Fresh, and Sophisticated' },
        { type: 'feature', text: 'Color psychology-driven generation using HSL color space with constrained hue, saturation, and lightness ranges' },
        { type: 'feature', text: '5 harmony types per mood: monochromatic, analogous, complementary, split-complementary, and tetrad' },
        { type: 'feature', text: 'Generate 3-8 colors per palette with consistent mood-based characteristics' },
        { type: 'improvement', text: 'Streamlined UI with button-based color count selector' },
        { type: 'improvement', text: 'Each generated palette can be saved, exported to visualizer, or regenerated for variations' },
      ]
    },
    {
      version: '1.7.6',
      date: 'October 16, 2025',
      title: 'Advanced Palette Generation & Export System',
      changes: [
        { type: 'feature', text: 'Polaroid-style image generation for social media sharing (1080x1200px)' },
        { type: 'feature', text: '100+ advanced palette generation algorithms across 7 categories' },
        { type: 'feature', text: 'Scientific palettes: OKLCH uniform spacing, LAB gradients, perceptually uniform lightness scales' },
        { type: 'feature', text: 'Data visualization palettes: sequential, categorical, diverging, and colorblind-safe options' },
        { type: 'feature', text: 'Mathematical palettes: Fibonacci, Golden Ratio, Lucas Numbers, Prime Steps, Pythagorean Intervals' },
        { type: 'feature', text: 'Cultural & historical palettes: Art Deco, Bauhaus, Memphis Design, Wes Anderson, Blade Runner' },
        { type: 'feature', text: 'Nature-inspired palettes: Peacock Feather, Coral Reef, Desert Biome, Ocean Depths, Dawn Sky' },
        { type: 'feature', text: 'Design system palettes: Material Design, Brand System, 60-30-10 Rule, Semantic Status Colors' },
        { type: 'feature', text: 'Gradient palettes: Bezier curves, ease-in/ease-out, multi-point gradients, radial expansion' },
        { type: 'improvement', text: 'Comprehensive export system: CSS, SCSS, JavaScript, SVG, Sketch, Adobe ASE, Polaroid PNG' },
      ]
    },
    {
      version: '1.7.5',
      date: 'August 30, 2025',
      title: 'Context Menus & UX Improvements',
      changes: [
        { type: 'improvement', text: 'Context menus now track position on scroll for better UX' },
        { type: 'improvement', text: 'Export dropdowns stay anchored to their trigger buttons during scroll' },
        { type: 'improvement', text: 'Enhanced color history tooltips with better positioning' },
        { type: 'fix', text: 'Fixed tooltip overflow issues in color history container' },
        { type: 'feature', text: 'Added Clear button to custom palette in toolbar' },
      ]
    },
    {
      version: '1.7.4',
      date: 'August 8, 2025',
      title: 'Custom Palette & State Management',
      changes: [
        { type: 'feature', text: 'Separated custom palette (toolbar) from palette builder for independent workflows' },
        { type: 'feature', text: 'Custom palette now persists in localStorage across browser sessions' },
        { type: 'feature', text: 'Added localStorage warning banner to Saved Palettes section' },
        { type: 'improvement', text: 'Added bottom padding to prevent toolbar from covering content' },
        { type: 'improvement', text: 'Enhanced toast notification consistency across the app' },
      ]
    },
    {
      version: '1.7.3',
      date: 'July 18, 2025',
      title: 'Color History Enhancements',
      changes: [
        { type: 'feature', text: 'Removed limit on color history - now tracks unlimited colors' },
        { type: 'feature', text: 'Added horizontal scroll with arrow navigation for color history' },
        { type: 'improvement', text: 'Enhanced color history UI with gradient backgrounds and animations' },
        { type: 'improvement', text: 'Added color count badge to history section' },
        { type: 'improvement', text: 'Improved tooltip styling consistency in dark mode' },
      ]
    },
    {
      version: '1.7.2',
      date: 'June 25, 2025',
      title: 'Interactive Visualizer',
      changes: [
        { type: 'feature', text: 'New interactive visualizer with real-time UI previews' },
        { type: 'feature', text: 'Multiple demo tabs: Images, Buttons, Cards, Forms, Tables, Text, Dashboard, Patterns, Graphical Elements, Templates' },
        { type: 'feature', text: 'Export colors directly to visualizer from any palette' },
        { type: 'improvement', text: 'Added color role assignment and drag-and-drop reordering' },
        { type: 'improvement', text: 'Real-time preview of colors in actual design components' },
      ]
    },
    {
      version: '1.7.1',
      date: 'June 15, 2025',
      title: 'Palette Management & Search',
      changes: [
        { type: 'feature', text: 'Search and filter saved palettes by name or color hex codes' },
        { type: 'feature', text: 'Inline palette renaming with keyboard shortcuts (Enter to save, Escape to cancel)' },
        { type: 'feature', text: 'Import/Export all palettes functionality (JSON format)' },
        { type: 'improvement', text: 'Enhanced saved palettes UI with compact export dropdown' },
        { type: 'improvement', text: 'Current custom palette display at top of saved palettes section' },
      ]
    },
    {
      version: '1.7.0',
      date: 'June 5, 2025',
      title: 'Image Color Extraction',
      changes: [
        { type: 'feature', text: 'Upload images and extract dominant colors instantly' },
        { type: 'feature', text: 'K-means clustering algorithm for accurate color extraction' },
        { type: 'feature', text: 'Adjustable number of colors to extract (2-10 colors)' },
        { type: 'feature', text: 'Save extracted palettes directly to your collection' },
        { type: 'improvement', text: 'Drag and drop support for image uploads' },
      ]
    },
    {
      version: '1.6.0',
      date: 'May 12, 2025',
      title: 'Eye Dropper & Export Formats',
      changes: [
        { type: 'feature', text: 'Added Eye Dropper tool for picking colors from your screen' },
        { type: 'feature', text: 'Export to CSS, SCSS, JavaScript, JSON, SVG, and more' },
        { type: 'feature', text: 'Download palettes as Sketch, Adobe ASE, and PNG formats' },
        { type: 'improvement', text: 'Enhanced export dropdown with categorized options' },
        { type: 'improvement', text: 'Toast notifications for successful exports and downloads' },
      ]
    },
    {
      version: '1.5.0',
      date: 'April 18, 2025',
      title: 'Palette Builder & Management',
      changes: [
        { type: 'feature', text: 'New Palette Builder for creating custom color combinations' },
        { type: 'feature', text: 'Save and manage multiple palettes locally' },
        { type: 'feature', text: 'Reorder colors within palettes via drag and drop' },
        { type: 'feature', text: 'Export all saved palettes at once' },
        { type: 'improvement', text: 'Enhanced palette cards with preview and metadata' },
      ]
    },
    {
      version: '1.4.0',
      date: 'March 28, 2025',
      title: 'Color Standards Expansion',
      changes: [
        { type: 'feature', text: 'Added 8 new color standards: IKEA, Copic, Prismacolor, Material Design, Tailwind' },
        { type: 'feature', text: 'British Standard, Automotive, and Munsell color matching' },
        { type: 'improvement', text: 'Enhanced color matching algorithm for better accuracy' },
        { type: 'improvement', text: 'Detailed tooltips showing color codes and names' },
        { type: 'performance', text: 'Optimized color standard search performance' },
      ]
    },
    {
      version: '1.3.0',
      date: 'March 5, 2025',
      title: 'Accessibility & Color Blindness',
      changes: [
        { type: 'feature', text: 'Real-time color blindness simulation (8 types)' },
        { type: 'feature', text: 'WCAG contrast ratio checker for all color pairs' },
        { type: 'feature', text: 'Accessibility compliance indicators (AA, AAA)' },
        { type: 'improvement', text: 'Automatic text color selection for optimal contrast' },
        { type: 'improvement', text: 'Visual warnings for low contrast combinations' },
      ]
    },
    {
      version: '1.2.0',
      date: 'February 14, 2025',
      title: 'Color Space Support',
      changes: [
        { type: 'feature', text: 'Added LAB, LCH, OKLAB, and OKLCH color space conversions' },
        { type: 'feature', text: 'HSB and CMYK color format support' },
        { type: 'feature', text: 'Color temperature and psychology analysis' },
        { type: 'improvement', text: 'More accurate color harmony generation' },
        { type: 'improvement', text: 'Enhanced shade and tint generation algorithms' },
      ]
    },
    {
      version: '1.1.0',
      date: 'January 22, 2025',
      title: 'Color Harmonies & Palettes',
      changes: [
        { type: 'feature', text: 'Generate 6 color harmony types: Monochromatic, Analogous, Complementary, Triadic, Tetradic, Split-Complementary' },
        { type: 'feature', text: 'Automatic shade generation (10 shades per color)' },
        { type: 'feature', text: 'Copy individual colors or entire palettes' },
        { type: 'improvement', text: 'Enhanced color picker with real-time preview' },
        { type: 'improvement', text: 'Tabbed interface for better palette organization' },
      ]
    },
    {
      version: '1.0.0',
      date: 'January 5, 2025',
      title: 'Initial Release',
      changes: [
        { type: 'feature', text: 'Professional color palette generator with interactive picker' },
        { type: 'feature', text: 'RAL, Pantone, HKS, NCS, and Federal Standard color matching' },
        { type: 'feature', text: 'HEX, RGB, and HSL color format support' },
        { type: 'feature', text: 'Dark mode support with smooth transitions' },
        { type: 'feature', text: 'Responsive design for all devices' },
        { type: 'feature', text: 'Color name identification (Wada color system)' },
      ]
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors duration-300"
    >
      <SEO
        title="Changelog - Komorebi Updates & Release Notes"
        description="Stay up to date with the latest features, improvements, and bug fixes in Komorebi. View our complete changelog with detailed release notes, new features, and product updates."
        canonical="https://ko-mo-re-bi.com/changelog"
        keywords="komorebi changelog, product updates, release notes, new features, bug fixes, version history, color tool updates"
        article={{
          publishedTime: "2024-09-03T00:00:00+00:00",
          modifiedTime: "2025-10-16T00:00:00+00:00",
          author: "https://obelisk.agency"
        }}
      />
      {/* Skip to content link for screen readers */}
      <a
        href="#changelog-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to content
      </a>

      {/* Animated Background Orbs - Reduced for performance */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div
          className="absolute w-[200px] h-[200px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] rounded-full bg-gradient-to-r from-blue-400/10 to-cyan-400/10 dark:from-purple-400/20 dark:to-pink-400/20 blur-2xl md:blur-3xl"
          style={{
            top: '10%',
            left: '5%',
            transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`,
            willChange: 'transform',
            transition: 'transform 0.3s ease-out'
          }}
        />
        <div
          className="absolute w-[200px] h-[200px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] rounded-full bg-gradient-to-r from-purple-400/10 to-pink-400/10 dark:from-blue-400/20 dark:to-cyan-400/20 blur-2xl md:blur-3xl"
          style={{
            bottom: '10%',
            right: '5%',
            transform: `translate(${mousePosition.x * -0.02}px, ${mousePosition.y * -0.02}px)`,
            willChange: 'transform',
            transition: 'transform 0.3s ease-out'
          }}
        />
      </div>

      {/* Header - Fade only, no movement */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="sticky top-4 z-50 px-4"
      >
        <div className="max-w-[1200px] mx-auto">
          <header
            role="banner"
            className={`relative border border-gray-200/50 dark:border-gray-800/50 bg-white/70 dark:bg-gray-950/70 ${isMobileMenuOpen ? 'rounded-3xl' : 'rounded-full'} ${isScrolled ? 'shadow-lg shadow-gray-200/50 dark:shadow-gray-900/50' : ''}`}
            style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', transition: 'box-shadow 300ms' }}
          >
            <div className="px-6 py-3 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" aria-label="Go to homepage">
                  <div className="relative" aria-hidden="true">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg blur-lg opacity-50" />
                    <img src="/komorebi-logo.svg" alt="Komorebi Logo" className="w-6 h-6 relative z-10" />
                  </div>
                  <span className="text-base font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">Komorebi</span>
                </Link>
                <nav role="navigation" aria-label="Main navigation" className="hidden md:flex items-center gap-2">
                  <Link to="/#features" className="px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-300" aria-label="View features">Features</Link>
                  <Link to="/story" className="px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-300" aria-label="Read our story">Story</Link>
                  <Link to="/changelog" className="px-4 py-1.5 text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full transition-all duration-300" aria-current="page">Changelog</Link>
                  <a
                    href="https://www.figma.com/community/plugin/1562754844917396329"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-300"
                    aria-label="Get Komorebi Figma Plugin"
                  >
                    <Puzzle className="w-3.5 h-3.5" />
                    Figma Plugin
                  </a>
                  <a
                    href="https://www.producthunt.com/posts/komorebi-color-play?utm_source=other&utm_medium=social"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-300"
                    aria-label="Review Komorebi on Product Hunt"
                  >
                    <Star className="w-3.5 h-3.5" />
                    Review
                  </a>
                </nav>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsDark(!isDark)}
                  className="px-3 py-1.5 rounded-full hover:bg-gray-200/80 dark:hover:bg-gray-800/80 backdrop-blur-sm transition-all duration-300 text-lg"
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-pressed={isDark}
                >
                  {isDark ? '☀️' : '🌙'}
                </button>
                <Link
                  to="/app"
                  className="hidden sm:inline-flex px-4 py-1.5 bg-gray-900/80 dark:bg-white/80 text-white dark:text-gray-900 rounded-full font-semibold hover:bg-gray-900 dark:hover:bg-white transition-all duration-300 hover:scale-105 border border-gray-800 dark:border-gray-200 text-sm"
                  style={{ backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
                  aria-label="Launch the Komorebi app"
                >
                  Launch App
                </Link>
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="md:hidden px-3 py-1.5 rounded-full hover:bg-gray-200/80 dark:hover:bg-gray-800/80 transition-all duration-300"
                  aria-label="Toggle mobile menu"
                  aria-expanded={isMobileMenuOpen}
                >
                  {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="md:hidden border-t border-gray-200 dark:border-gray-800 py-4 px-6 rounded-b-2xl"
              >
                <nav className="flex flex-col gap-2" role="navigation" aria-label="Mobile navigation">
                  <Link
                    to="/#features"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300"
                  >
                    Features
                  </Link>
                  <Link
                    to="/story"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300"
                  >
                    Story
                  </Link>
                  <Link
                    to="/changelog"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2.5 text-sm font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50 rounded-lg"
                  >
                    Changelog
                  </Link>
                  <a
                    href="https://www.figma.com/community/plugin/1562754844917396329"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300"
                  >
                    <Puzzle className="w-4 h-4" />
                    Figma Plugin
                  </a>
                  <a
                    href="https://www.producthunt.com/posts/komorebi-color-play?utm_source=other&utm_medium=social"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300"
                  >
                    <Star className="w-4 h-4" />
                    Review on Product Hunt
                  </a>
                  <Link
                    to="/app"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="mt-2 px-4 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg font-semibold text-center border border-gray-800 dark:border-gray-200 text-sm transition-all duration-300"
                  >
                    Launch App
                  </Link>
                </nav>
              </motion.div>
            )}
          </header>
        </div>
      </motion.div>

      {/* Hero Section - Slide up with fade */}
      <motion.section
        role="main"
        aria-labelledby="changelog-title"
        initial={{ opacity: 0, ...(isSafari ? {} : { y: 30 }) }}
        animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
        exit={{ opacity: 0, ...(isSafari ? {} : { y: -30 }) }}
        transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.1, ease: "easeInOut" }}
        className="relative max-w-5xl mx-auto px-6 py-24 md:py-32 text-center"
      >
        <div className="space-y-6 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 shadow-lg" aria-hidden="true">
            <Clock className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium">Product Updates & Release Notes</span>
          </div>

          <h1 id="changelog-title" className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight text-gray-900/90 dark:text-white/90" style={{ lineHeight: '1.3' }}>
            <span className="inline-block animate-morph-gradient font-black">Changelog</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Track all updates, new features, improvements, and bug fixes. We're constantly evolving to make Komorebi the best color tool for creators.
          </p>
        </div>
      </motion.section>

      {/* Changelog Timeline - Staggered animation */}
      <motion.section
        id="changelog-content"
        aria-labelledby="changelog-title"
        initial={{ opacity: 0, ...(isSafari ? {} : { y: 30 }) }}
        animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
        exit={{ opacity: 0, ...(isSafari ? {} : { y: -30 }) }}
        transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.2, ease: "easeInOut" }}
        className="relative max-w-4xl mx-auto px-6 py-12 pb-24"
      >
        <div className="space-y-12" role="list" aria-label="Version history timeline">
          {changelog.map((release, index) => (
            <div key={index} className="relative group" role="listitem">
              {/* Timeline Line */}
              {index !== changelog.length - 1 && (
                <div className="absolute left-[20px] top-16 bottom-0 w-0.5 bg-gradient-to-b from-blue-500/50 to-transparent dark:from-blue-400/50" aria-hidden="true" />
              )}

              {/* Timeline Dot */}
              <div className="absolute left-0 top-6 w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 z-10" aria-hidden="true">
                <Sparkles className="w-5 h-5 text-white" />
              </div>

              {/* Content Card */}
              <div className="ml-20">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 dark:from-blue-400/20 dark:to-cyan-400/20 rounded-2xl blur-xl opacity-50 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true" />
                  <article
                    className="relative p-8 rounded-2xl bg-white/60 dark:bg-gray-800/60 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-all duration-300"
                    style={{ backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
                  >
                    {/* Version & Date */}
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="text-2xl font-bold text-gray-900 dark:text-white">
                        v{release.version}
                      </span>
                      <time dateTime={release.date} className="px-3 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                        {release.date}
                      </time>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                      {release.title}
                    </h3>

                    {/* Changes List */}
                    <ul className="space-y-3" aria-label={`Changes in version ${release.version}`}>
                      {release.changes.map((change, changeIndex) => (
                        <li key={changeIndex} className="flex items-start gap-3">
                          <span className={`flex-shrink-0 px-2 py-1 rounded-md ${getColorForType(change.type)} flex items-center gap-1`} aria-label={`${change.type} update`}>
                            {getIconForType(change.type)}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300 leading-relaxed pt-0.5">
                            {change.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </article>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* End of Timeline */}
        <div className="mt-12 text-center" role="status" aria-live="polite">
          <div
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/60 dark:bg-gray-800/60 border border-gray-300 dark:border-gray-700 shadow-lg"
            style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
          >
            <CheckCircle className="w-5 h-5 text-green-500" aria-hidden="true" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              You've reached the beginning! More updates coming soon.
            </span>
          </div>
        </div>
      </motion.section>

      {/* CTA Section - Fade and scale */}
      <motion.section
        aria-labelledby="cta-heading"
        initial={{ opacity: 0, ...(isSafari ? {} : { scale: 0.95 }) }}
        animate={{ opacity: 1, ...(isSafari ? {} : { scale: 1 }) }}
        exit={{ opacity: 0, ...(isSafari ? {} : { scale: 0.95 }) }}
        transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.3, ease: "easeInOut" }}
        className="relative max-w-5xl mx-auto px-6 py-20 mb-20"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-gray-400/20 dark:bg-gray-400/30 rounded-3xl blur-2xl opacity-50" aria-hidden="true" />
          <div className="relative p-12 md:p-16 rounded-3xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-center overflow-hidden border border-gray-800 dark:border-gray-200">
            <div className="relative z-10">
              <h2 id="cta-heading" className="text-4xl md:text-5xl font-bold mb-4">
                Try the Latest Features
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Experience all the new improvements and features we've been working on.
              </p>
              <Link
                to="/app"
                className="inline-flex items-center gap-2 px-10 py-4 bg-white/90 dark:bg-gray-900/90 text-gray-900 dark:text-white rounded-xl font-bold text-lg hover:bg-white dark:hover:bg-gray-900 transition-all duration-300 hover:scale-105 border border-gray-200 dark:border-gray-800"
                style={{ backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
                aria-label="Launch Komorebi app for free"
              >
                <Sparkles className="w-5 h-5" aria-hidden="true" />
                Launch App Free
              </Link>
              <p className="text-sm mt-6 opacity-75">
                Share your creations with <span className="font-semibold">#komorebicolorplay</span>
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Footer - Slide up from bottom */}
      <motion.footer
        role="contentinfo"
        initial={{ opacity: 0, ...(isSafari ? {} : { y: 20 }) }}
        animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
        exit={{ opacity: 0, ...(isSafari ? {} : { y: 20 }) }}
        transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.4, ease: "easeInOut" }}
        className="relative border-t border-gray-200/50 dark:border-gray-800/50 bg-white/30 dark:bg-gray-950/30 py-12"
        style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to homepage">
              <img src="/komorebi-logo.svg" alt="Komorebi Logo" className="w-6 h-6" />
              <span className="font-semibold">Komorebi</span>
            </Link>
            <nav role="navigation" aria-label="Footer navigation" className="flex gap-6 text-sm text-gray-600 dark:text-gray-400">
              <Link to="/#features" className="hover:text-blue-600 transition-colors duration-300" aria-label="View features">Features</Link>
              <Link to="/story" className="hover:text-blue-600 transition-colors duration-300" aria-label="Read our story">Story</Link>
              <Link to="/changelog" className="hover:text-blue-600 transition-colors duration-300" aria-label="View changelog">Changelog</Link>
              <Link to="/app" className="hover:text-blue-600 transition-colors duration-300" aria-label="Launch the app">App</Link>
              <a href="mailto:hello@obelisk.agency" className="hover:text-blue-600 transition-colors duration-300" aria-label="Contact us">Contact</a>
            </nav>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span>made with</span>
              <Heart className="h-4 w-4 text-red-500 fill-current" aria-hidden="true" />
              <span>and</span>
              <Coffee className="h-4 w-4 text-amber-600" aria-hidden="true" />
              <span>by</span>
              <a
                href="https://obelisk.agency"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200"
                aria-label="Visit Obelisk's website"
              >
                obelisk
              </a>
            </div>
          </div>
        </div>
      </motion.footer>
    </motion.div>
  );
};

export default Changelog;
